import { subscribe, definitions } from '../../message-broker';
import { callEndpoint, telemetry } from '../../backend';
import { transitionToNextRoute } from '../../route/route-navigation';
import { ROUTE_MARKER_SUMMARY } from '../../route/route-names';
import { getStore } from '../../store';

import {
  layerExists,
  setDataSource,
  sourceExists,
  toggleLayerIfRequired,
} from '../_helpers';
import isMapVisible from '../../../components/helpers/is-map-visible';
import { hasFeatureEnabled } from '../../feature';
import { loadPinWrapImages } from '../pin-wrap-images';

let mapReference;
let selectedMarkerId = null;
let requestDataTimeout;
let currentViewPort;
let lastQueryViewPort;

const addLayersIfRequired = () => {
  if (!mapReference) return;

  if (!sourceExists(mapReference, 'storyMarker')) {
    mapReference.addSource('storyMarker', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [],
      },
    });
  }

  if (!layerExists(mapReference, 'storyMarker')) {
    mapReference.addLayer({
      id: 'storyMarker',
      type: 'symbol',
      source: 'storyMarker',
      layout: {
        'icon-image': ['to-string', ['get', 'image']],
        'icon-size': [
          'interpolate',
          ['linear'],
          ['zoom'],
          8.4999,
          ['case', ['==', ['get', 'markerSelected'], true], 0.5, 0],
          8.5,
          ['case', ['==', ['get', 'markerSelected'], true], 0.5, 0.25],
          11,
          ['case', ['==', ['get', 'markerSelected'], true], 0.5, 0.25],
          14,
          ['case', ['==', ['get', 'markerSelected'], true], 0.5, 0.4],
        ],
        'icon-anchor': 'bottom',
        'icon-allow-overlap': true,
        'text-allow-overlap': true,
      },
      paint: {},
    });
  }
};

const layer = {
  displayName: 'Story Markers',
  shareText: 'Check out this story on Deckee!',
  standardMarkerOptions: {
    markerType: 'storyMarker',
    allowCreation: true,
    allowEditing: false,
    allowComments: false,
    viewOptions: {
      typeTitle: 'My Marker',
      itemTitleFunction: (item) => (item && item.title) || 'Marker',
    },
    creationOptions: {
      menuText: 'My Marker',
      newTitle: 'My Marker',
      titlePlaceholder: 'My Marker',
      checkBoxItems: [],

      // This is the text that is displayed at the top of the create story marker form
      // We are leaving blank atm
      markerTypeDescription: null,
    },
    editingOptions: {},
  },
  synonyms: '',
  group: 'imageMarker',
  typeUrl: 'story-markers',
  icon: null,
  shownByDefault: true,
  relativeDepth: 10,
  menuOptions: {
    visible: false,
    // eslint-disable-next-line no-warning-comments
    owningMenu: 'marine-points-of-interest', // todo: is this the right value?
    order: 0,
  },
  popOverConfiguration: (marker) => ({
    id: marker.id,
    title: marker.title,
    shareText: 'Check out this story on Deckee!',
    description: marker.description,
  }),
};

const loadMarkerImages = async (imageIds) => {
  // Influencer avatars are the story-marker icon. Failed ids are remembered
  // for the session in pin-wrap-images.ts.
  await loadPinWrapImages(mapReference, imageIds);
};

const getStoryMarkers = async () => {
  try {
    // If there isn't a view port to query for bounds, don't do anything.
    const influencerMarkersEnabled = hasFeatureEnabled('influencer-marker');
    if ((!currentViewPort || !influencerMarkersEnabled) && isMapVisible())
      return [];
    const results = await callEndpoint(
      callEndpoint.endpoints.mapSearchQueryByBoundingBoxAndType,
      {
        mapMarkerTypes: ['storyMarker'],
        boundingBox: currentViewPort.bounds,
      },
    );

    const uniqueMarkerImageIds = [
      ...new Set(results.map((marker) => marker.owningInfluencer.image)),
    ];

    await loadMarkerImages(uniqueMarkerImageIds);

    return results || [];
  } catch (error) {
    telemetry(
      telemetry.WARN,
      `[storyMarker mapLayer] Failed to load story markers, ${error}`,
    );
    return [];
  }
};

layer.onInitialize = (map) => {
  mapReference = map;
  addLayersIfRequired();

  map.on('deckee-press', 'storyMarker', async (event) => {
    // This is to select a marker and go to the popover
    const { id } = event.features[0].properties;
    if (!event.originalEvent.defaultPrevented) {
      // Prevent the map click event from propagating to other layers
      event.originalEvent.preventDefault();
      const data = await getStoryMarkers();
      const geojson = {
        type: 'FeatureCollection',
        features: data.map((item) => ({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [item.latLon.lon, item.latLon.lat],
          },
          properties: {
            ...item,
            image: item.owningInfluencer.image,
            markerSelected: id === item.id,
          },
        })),
      };

      selectedMarkerId = id;

      setDataSource(mapReference, 'storyMarker', geojson);

      telemetry(
        telemetry.DEBUG,
        `[storyMarkerEvent] event on storyMarker of id: '${id}': ${Object.keys(
          event,
        )}`,
      );
      transitionToNextRoute(ROUTE_MARKER_SUMMARY, {
        markerType: 'storyMarker',
        id,
      });
    }
  });
};

const updateData = async (force) => {
  // If we have yet to receive a reference to the map object through the onInitialize function,
  // just return as we can't update the data on the layer if the layer doesn't exist yet.
  if (!mapReference) return;
  addLayersIfRequired();

  const { hidingAllLayers } = getStore().getState().appControls;

  if (hidingAllLayers) {
    toggleLayerIfRequired(mapReference, 'storyMarker', false);
    return;
  }
  toggleLayerIfRequired(mapReference, 'storyMarker', true);

  // If there isn't a view port to query for bounds, don't do anything.
  if (!currentViewPort) return;

  // If we're not forcing the update, and the bounds are the same as the
  // previous call, don't do anything
  if (!force && currentViewPort && currentViewPort.bounds === lastQueryViewPort)
    return;
  lastQueryViewPort = currentViewPort && currentViewPort.bounds;

  // Update the data for this layer every minute when the viewport doesn't change.
  if (requestDataTimeout) clearTimeout(requestDataTimeout);
  requestDataTimeout = setTimeout(() => updateData(true), 60 * 1000);

  const data = await getStoryMarkers();

  const geojson = {
    type: 'FeatureCollection',
    features: data.map((item) => ({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [item.latLon.lon, item.latLon.lat],
      },
      properties: {
        ...item,
        image: item.owningInfluencer.image,
        // This is so it doesn't deselect when reloading data
        markerSelected: selectedMarkerId === item.id,
      },
    })),
  };

  setDataSource(mapReference, 'storyMarker', geojson);
};

layer.onToggle = (map, show) => {
  toggleLayerIfRequired(map, 'storyMarker', show);
};

subscribe(definitions.map.viewPortUpdated, (viewPort) => {
  currentViewPort = viewPort;
  updateData();
});

export default layer;
